import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  Alert,
  Divider,
  IconButton,
  Stack,
  Chip,
} from '@mui/material';
import {
  Message as MessageIcon,
  Wallpaper as WallpaperIcon,
  RestartAlt,
  SystemUpdate,
  Translate,
  Add,
  DeleteOutline,
  Close,
  Link as LinkIcon,
  Phone,
  GridView,
} from '@mui/icons-material';
import { tokens } from '../../theme';

// ─── Configuración por tipo de comando ────────────────────────────────────────
const CMD_META = {
  message:       { label: 'Enviar mensaje',        Icon: MessageIcon,   color: tokens.color.semantic.info },
  wallpaper:     { label: 'Cambiar fondo',          Icon: WallpaperIcon, color: tokens.color.brand[500] },
  reboot:        { label: 'Reiniciar dispositivo',  Icon: RestartAlt,    color: tokens.color.semantic.warning },
  update_app:    { label: 'Actualizar aplicación',  Icon: SystemUpdate,  color: tokens.color.brand[500] },
  home_shortcut: { label: 'Configurar accesos',     Icon: GridView,      color: tokens.color.brand[500] },
  set_language:  { label: 'Cambiar idioma',         Icon: Translate,     color: tokens.color.brand[500] },
};

const LANGUAGES = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'Inglés' },
  { value: 'ca', label: 'Catalán' },
  { value: 'gl', label: 'Gallego' },
  { value: 'eu', label: 'Euskera' },
];

// ─── Formularios por tipo ─────────────────────────────────────────────────────

const MessageForm = ({ value, onChange, error }) => (
  <TextField
    autoFocus
    fullWidth
    multiline
    rows={4}
    label="Mensaje"
    placeholder="Escribe el mensaje que verán en la tablet..."
    value={value}
    onChange={(e) => onChange(e.target.value)}
    error={!!error}
    helperText={error || `${value.length}/300 caracteres`}
    inputProps={{ maxLength: 300 }}
  />
);

const WallpaperForm = ({ value, onChange, error }) => (
  <Box>
    <TextField
      autoFocus
      fullWidth
      label="URL de la imagen"
      placeholder="https://ejemplo.com/imagen.jpg"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={!!error}
      helperText={error || 'Formatos soportados: JPG, PNG, WebP'}
      InputProps={{
        startAdornment: (
          <LinkIcon sx={{ color: tokens.color.ink.muted, mr: 1, fontSize: 18 }} />
        ),
      }}
    />
    {value && (
      <Box
        sx={{
          mt: 2,
          height: 140,
          borderRadius: tokens.radius.md,
          border: `1px solid ${tokens.color.border.default}`,
          overflow: 'hidden',
          backgroundColor: tokens.color.surface.subtle,
        }}
      >
        <img
          src={value}
          alt="Preview"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      </Box>
    )}
  </Box>
);

const RebootForm = ({ deviceCount }) => (
  <Alert severity="warning" sx={{ mt: 1 }}>
    Se va a reiniciar <strong>{deviceCount} dispositivo{deviceCount !== 1 ? 's' : ''}</strong>.
    El proceso tarda entre 30 y 60 segundos. Las tablets desconectadas recibirán
    el comando cuando vuelvan a conectarse.
  </Alert>
);

const UpdateAppForm = ({ url, versionCode, onUrlChange, onVersionChange, errors }) => (
  <Stack spacing={2}>
    <TextField
      autoFocus
      fullWidth
      label="URL del APK"
      placeholder="https://praxysrd.com/app-release.apk"
      value={url}
      onChange={(e) => onUrlChange(e.target.value)}
      error={!!errors.url}
      helperText={errors.url || 'Debe ser accesible desde las tablets'}
      InputProps={{
        startAdornment: (
          <LinkIcon sx={{ color: tokens.color.ink.muted, mr: 1, fontSize: 18 }} />
        ),
      }}
    />
    <TextField
      fullWidth
      label="Código de versión"
      placeholder="ej: 12"
      type="number"
      value={versionCode}
      onChange={(e) => onVersionChange(e.target.value)}
      error={!!errors.versionCode}
      helperText={errors.versionCode || 'Entero mayor que la versión instalada actualmente'}
      inputProps={{ min: 1 }}
    />
  </Stack>
);

const LanguageForm = ({ value, onChange }) => (
  <TextField
    autoFocus
    select
    fullWidth
    label="Idioma de la interfaz"
    value={value}
    onChange={(e) => onChange(e.target.value)}
  >
    {LANGUAGES.map((l) => (
      <MenuItem key={l.value} value={l.value}>
        {l.label}
      </MenuItem>
    ))}
  </TextField>
);

const newShortcut = () => ({
  id: `sc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  type: 'web',
  name: '',
  target: '',
  icon_image: '',
  inner_circle_photo: '',
  enabled: true,
});

const ShortcutItem = ({ sc, onChange, onRemove }) => (
  <Box
    sx={{
      border: `1px solid ${tokens.color.border.default}`,
      borderRadius: tokens.radius.md,
      p: 2,
      backgroundColor: tokens.color.surface.default,
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, alignItems: 'center' }}>
      <Chip
        label={sc.type === 'contact' ? 'Contacto' : 'Web'}
        size="small"
        sx={{
          backgroundColor: sc.type === 'contact' ? tokens.color.status.pending.bg : tokens.color.brand[50],
          color: sc.type === 'contact' ? tokens.color.status.pending.text : tokens.color.brand[600],
          border: `1px solid ${sc.type === 'contact' ? tokens.color.status.pending.border : tokens.color.brand[200]}`,
          fontWeight: 600,
        }}
      />
      <IconButton size="small" onClick={() => onRemove(sc.id)} sx={{ color: tokens.color.ink.muted }}>
        <DeleteOutline sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>

    <Stack spacing={1.5}>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <TextField
          select
          label="Tipo"
          value={sc.type}
          onChange={(e) => onChange(sc.id, { type: e.target.value })}
          sx={{ width: 110, flexShrink: 0 }}
        >
          <MenuItem value="web">Web</MenuItem>
          <MenuItem value="contact">Contacto</MenuItem>
        </TextField>
        <TextField
          fullWidth
          label="Nombre visible"
          value={sc.name}
          onChange={(e) => onChange(sc.id, { name: e.target.value })}
          placeholder="Portal Salud"
        />
      </Box>

      <TextField
        fullWidth
        label={sc.type === 'contact' ? 'Número de teléfono' : 'URL de destino'}
        value={sc.target}
        onChange={(e) => onChange(sc.id, { target: e.target.value })}
        placeholder={sc.type === 'contact' ? '+18095551234' : 'https://portal.ejemplo.com'}
        InputProps={{
          startAdornment: sc.type === 'contact'
            ? <Phone sx={{ color: tokens.color.ink.muted, mr: 1, fontSize: 18 }} />
            : <LinkIcon sx={{ color: tokens.color.ink.muted, mr: 1, fontSize: 18 }} />,
        }}
      />

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <TextField
          fullWidth
          label="Icono (URL imagen)"
          value={sc.icon_image}
          onChange={(e) => onChange(sc.id, { icon_image: e.target.value })}
          placeholder="https://..."
        />
        <TextField
          fullWidth
          label="Foto interior (URL)"
          value={sc.inner_circle_photo}
          onChange={(e) => onChange(sc.id, { inner_circle_photo: e.target.value })}
          placeholder="https://..."
        />
      </Box>
    </Stack>
  </Box>
);

const HomeShortcutForm = ({ primary, onPrimaryChange, shortcuts, onAddShortcut, onUpdateShortcut, onRemoveShortcut, errors }) => (
  <Stack spacing={3}>
    <Box>
      <Typography variant="overline" sx={{ mb: 1.5, display: 'block' }}>
        Botón principal (Empresa Salud)
      </Typography>
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField
            fullWidth
            label="Nombre visible"
            value={primary.visible_name}
            onChange={(e) => onPrimaryChange({ visible_name: e.target.value })}
            placeholder="Empresa Salud"
          />
          <TextField
            select
            label="Tipo"
            value={primary.button_type}
            onChange={(e) => onPrimaryChange({ button_type: e.target.value })}
            sx={{ width: 160, flexShrink: 0 }}
          >
            <MenuItem value="web">Abrir web</MenuItem>
            <MenuItem value="tel">Teléfono</MenuItem>
            <MenuItem value="intent">Intent personalizado</MenuItem>
          </TextField>
        </Box>
        <TextField
          fullWidth
          label="URL o destino"
          value={primary.target}
          onChange={(e) => onPrimaryChange({ target: e.target.value })}
          placeholder={primary.button_type === 'tel' ? 'tel:+18095551234' : 'https://upmedica.com'}
          error={!!errors?.primaryTarget}
          helperText={errors?.primaryTarget}
          InputProps={{
            startAdornment: <LinkIcon sx={{ color: tokens.color.ink.muted, mr: 1, fontSize: 18 }} />,
          }}
        />
        <TextField
          fullWidth
          label="Logo (URL imagen)"
          value={primary.icon_logo}
          onChange={(e) => onPrimaryChange({ icon_logo: e.target.value })}
          placeholder="https://upmedica.com/logo.png"
        />
      </Stack>
    </Box>

    <Divider />

    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="overline">
          Accesos personalizados ({shortcuts.length})
        </Typography>
        <Button size="small" startIcon={<Add />} onClick={onAddShortcut} variant="outlined">
          Agregar
        </Button>
      </Box>

      {shortcuts.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 3,
            borderRadius: tokens.radius.md,
            border: `1px dashed ${tokens.color.border.strong}`,
            color: tokens.color.ink.muted,
            fontSize: '0.875rem',
          }}
        >
          Sin accesos. Pulsa "Agregar" para añadir uno.
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {shortcuts.map((sc) => (
            <ShortcutItem
              key={sc.id}
              sc={sc}
              onChange={onUpdateShortcut}
              onRemove={onRemoveShortcut}
            />
          ))}
        </Stack>
      )}
    </Box>
  </Stack>
);

// ─── CommandDialog ─────────────────────────────────────────────────────────────
const CommandDialog = ({ open, commandType, devices = [], onClose, onExecute }) => {
  const [message, setMessage]           = useState('');
  const [wallpaperUrl, setWallpaperUrl] = useState('');
  const [apkUrl, setApkUrl]             = useState('');
  const [versionCode, setVersionCode]   = useState('');
  const [language, setLanguage]         = useState('es');
  const [primary, setPrimary]           = useState({
    visible_name: 'Empresa Salud',
    button_type: 'web',
    target: '',
    icon_logo: '',
  });
  const [shortcuts, setShortcuts] = useState([]);
  const [errors, setErrors]       = useState({});
  const [loading, setLoading]     = useState(false);

  // Reset al cambiar tipo/dispositivos
  useEffect(() => {
    if (!open) return;
    setMessage('');
    setWallpaperUrl('');
    setApkUrl('');
    setVersionCode('');
    setLanguage('es');
    setPrimary({ visible_name: 'Empresa Salud', button_type: 'web', target: '', icon_logo: '' });
    setShortcuts([]);
    setErrors({});
    setLoading(false);
  }, [open, commandType]);

  const meta = CMD_META[commandType] || {};
  const deviceCount = devices.length;
  const deviceLabel = deviceCount === 1
    ? (devices[0]?.device_name || devices[0]?.device_id || 'este dispositivo')
    : `${deviceCount} dispositivos`;

  const validate = () => {
    const errs = {};
    if (commandType === 'message' && !message.trim()) {
      errs.message = 'El mensaje no puede estar vacío';
    }
    if (commandType === 'wallpaper' && !wallpaperUrl.trim()) {
      errs.wallpaperUrl = 'Introduce una URL válida';
    }
    if (commandType === 'update_app') {
      if (!apkUrl.trim()) errs.url = 'La URL del APK es obligatoria';
      if (!versionCode || isNaN(Number(versionCode)) || Number(versionCode) < 1) {
        errs.versionCode = 'Introduce un número entero mayor que 0';
      }
    }
    if (commandType === 'home_shortcut' && !primary.target.trim()) {
      errs.primaryTarget = 'El destino del botón principal es obligatorio';
    }
    return errs;
  };

  const buildPayload = () => {
    switch (commandType) {
      case 'message':
        return { message: message.trim(), flash: false };
      case 'wallpaper':
        return { wallpaper_url: wallpaperUrl.trim() };
      case 'reboot':
        return {};
      case 'update_app':
        return { download_url: apkUrl.trim(), version_code: Number(versionCode) };
      case 'set_language':
        return { language_code: language };
      case 'home_shortcut': {
        const normalizedShortcuts = shortcuts
          .map((s) => ({
            id: s.id.trim(),
            type: s.type.trim(),
            name: s.name.trim(),
            target: s.target.trim(),
            icon_image: s.icon_image.trim(),
            inner_circle_photo: s.inner_circle_photo.trim(),
            enabled: s.enabled !== false,
          }))
          .filter((s) => s.id && s.name && s.target);
        return {
          primary: {
            visible_name: primary.visible_name.trim() || 'Empresa Salud',
            button_type: primary.button_type,
            target: primary.target.trim(),
            icon_logo: primary.icon_logo.trim(),
          },
          shortcuts: normalizedShortcuts,
        };
      }
      default:
        return {};
    }
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setLoading(true);
    try {
      const payload = buildPayload();
      await onExecute(commandType, devices, payload);
      onClose();
    } catch {
      // el componente padre maneja el error
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => {
    switch (commandType) {
      case 'message':
        return <MessageForm value={message} onChange={setMessage} error={errors.message} />;
      case 'wallpaper':
        return <WallpaperForm value={wallpaperUrl} onChange={setWallpaperUrl} error={errors.wallpaperUrl} />;
      case 'reboot':
        return <RebootForm deviceCount={deviceCount} />;
      case 'update_app':
        return (
          <UpdateAppForm
            url={apkUrl}
            versionCode={versionCode}
            onUrlChange={setApkUrl}
            onVersionChange={setVersionCode}
            errors={errors}
          />
        );
      case 'set_language':
        return <LanguageForm value={language} onChange={setLanguage} />;
      case 'home_shortcut':
        return (
          <HomeShortcutForm
            primary={primary}
            onPrimaryChange={(patch) => setPrimary((p) => ({ ...p, ...patch }))}
            shortcuts={shortcuts}
            onAddShortcut={() => setShortcuts((prev) => [...prev, newShortcut()])}
            onUpdateShortcut={(id, patch) =>
              setShortcuts((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)))
            }
            onRemoveShortcut={(id) =>
              setShortcuts((prev) => prev.filter((s) => s.id !== id))
            }
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  const isSubmitDisabled = loading || !commandType || (commandType === 'update_app' && (!apkUrl || !versionCode));

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth={commandType === 'home_shortcut' ? 'md' : 'sm'}
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {meta.Icon && (
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: tokens.radius.md,
                  backgroundColor: tokens.color.brand[50],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: tokens.color.brand[500],
                  flexShrink: 0,
                  '& .MuiSvgIcon-root': { fontSize: 18 },
                }}
              >
                <meta.Icon />
              </Box>
            )}
            <Box>
              <Typography variant="h5">{meta.label}</Typography>
              <Typography variant="caption" sx={{ color: tokens.color.ink.muted }}>
                Destino: {deviceLabel}
              </Typography>
            </Box>
          </Box>
          {!loading && (
            <IconButton size="small" onClick={onClose} sx={{ ml: 1 }}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ py: 1 }}>
          {renderForm()}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSubmitDisabled}>
          {commandType === 'reboot' ? 'Reiniciar' : 'Enviar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommandDialog;
